import { BehaviorSubject } from "rxjs";
import APIResponse from "../models/responses/APIResponse";
import Token from "../models/Token";
import { Endpoints } from "../helpers/Endpoints";
import { Globals } from "../helpers/globals";
import { setApiToken } from "../helpers/setupApis";
import { EmptyGuid, TylerUser, UserEntity } from "../models";
import { TylerAdminService } from "./ClientService";
import { TylerUserDetailedResponse } from "../models/swagger/generated-client-service";

const currentUserSubject = new BehaviorSubject(
    JSON.parse(localStorage.getItem("currentUser")!)
);

const portalSubject = new BehaviorSubject(localStorage.getItem("portal")!);

export const authenticationService = {
    login,
    refreshToken,
    logout,
    fromPlugin,
    tylerLogin,
    currentUser: currentUserSubject.asObservable(),
    portal: portalSubject.asObservable(),
    get currentUserValue() {
        return currentUserSubject.value;
    },
    get portalValue() {
        return portalSubject.value;
    },
};

function setTylerUserAsCurrent(state: string, tylerUser: TylerUser) {
    const user = {
        id: tylerUser.id!,
        username: tylerUser.username!,
        authToken: tylerUser.authToken!,
        firstName: tylerUser.firstName!,
        lastName: tylerUser.lastName!,
        organizationID: "5BE7B393-3E05-4EF1-9A1A-BC4323D2734C",
    } as UserEntity;
    localStorage.setItem("portal", state);
    portalSubject.next(state);
    localStorage.setItem("currentUser", JSON.stringify(user));
    currentUserSubject.next(user);
}

async function tylerLogin(state: string, username: string, password: string) {
    const request_params = JSON.parse(
        JSON.stringify({
            state: state,
            username: username,
            password: password,
        })
    );

    console.log(request_params);

    return TylerAdminService.login({ body: request_params })
        .then((res) => {
            setTylerUserAsCurrent(state, res.data!);
            console.log({
                "Data logged when accessing the middleware": res.data!,
            });
            setApiToken();
            return res;
        })
        .then((d) => {
            console.log("middleware complete");
            return d;
        });
}

function fromPlugin(userID: string): Promise<Token | null> {
    const requestOptions = {
        method: "GET",
        //  mode: "cors" as RequestMode,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            userID,
        }),
    };

    return fetch(Endpoints.ClientServices + "/api/users/get", requestOptions)
        .then((response) => {
            if (!response.ok) {
                if ([401, 403].indexOf(response.status) !== -1) {
                    // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
                    authenticationService.logout();
                    window.location.reload();
                }
            }
            return response.json().then((data) => data as APIResponse<Token>);
        })
        .then(async (apiResponse) => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem(
                "currentUser",
                JSON.stringify(apiResponse.data)
            );
            currentUserSubject.next(apiResponse.data);

            setApiToken();
            await Globals.retrieveMe();

            return apiResponse.data;
        });
}

async function login(
    username: string,
    password: string,
    FEATURE_FLAG?: string
): Promise<APIResponse<Token> | null> {
    console.log({
        "Auth 0 feature flag set in header: (AUTH_0_FEATURE_FLAG_HEADER)":
            FEATURE_FLAG,
    });
    const requestOptions = {
        method: "POST",
        //  mode: "cors" as RequestMode,
        headers: {
            "Content-Type": "application/json",
            AUTH_0_FEATURE_FLAG_HEADER: FEATURE_FLAG ?? "false",
        },
        body: JSON.stringify({
            username,
            password,
            organizationId: "5BE7B393-3E05-4EF1-9A1A-BC4323D2734C", //"3082E418-C3CA-4E23-36E8-08D99347B4B1"
        }),
    };

    return fetch(Endpoints.ClientServices + "/api/users/login", requestOptions)
        .then((response) => {
            if (!response.ok) {
                if ([401, 403].indexOf(response.status) !== -1) {
                    // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
                    authenticationService.logout();
                    window.location.reload();
                }
            }
            return response.json().then((data) => data as APIResponse<Token>);
        })
        .then(async (apiResponse) => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (apiResponse.successful == true) {
                localStorage.setItem(
                    "currentUser",
                    JSON.stringify(apiResponse.data)
                );
                currentUserSubject.next(apiResponse.data);

                setApiToken();
                await Globals.retrieveMe();
            }

            return apiResponse;
        });
}

function refreshToken(): Promise<Token | null> {
    const currentUser = authenticationService.currentUserValue;
    const portal = authenticationService.portalValue;

    if (portal) {
        const requestOptions = {
            method: "GET",
            //  mode: "cors" as RequestMode,
            headers: {
                "content-type": "application/json",
                "Tyler-Username": currentUser.username,
                "Tyler-Authorization-Hash": currentUser.authToken,
                "Tyler-State": portal,
            },
        };

        return fetch(
            Endpoints.ClientServices + "/api/tyler/login",
            requestOptions
        )
            .then((response) => {
                console.log(response);
                if (!response.ok) {
                    authenticationService.logout();
                    window.location.reload();
                }
                return response
                    .json()
                    .then((data) => data as TylerUserDetailedResponse);
            })
            .then(async (detailed) => {
                if (detailed.successful && detailed.data) {
                    setTylerUserAsCurrent(portal, detailed.data);
                } else {
                    authenticationService.logout();
                    window.location.reload();
                }
            })
            .then(() => {
                setApiToken();
                return currentUserSubject.getValue();
            });
    }

    const requestOptions = {
        method: "GET",
        //  mode: "cors" as RequestMode,
        headers: {
            "content-type": "application/json",
            "X-User-AuthToken": currentUser.authToken,
            "X-User-AccessToken": currentUser.accessToken,
            "X-User-RefreshToken": currentUser.refreshToken,
            AUTH_0_FEATURE_FLAG_HEADER:
                localStorage.getItem("AUTH_0_FEATURE_FLAG") ?? "false",
        },
    };

    return fetch(
        Endpoints.ClientServices + "/api/users/refresh",
        requestOptions
    )
        .then((response) => {
            console.log(response);
            if (!response.ok) {
                authenticationService.logout();
                window.location.reload();
            }
            return response.json().then((data) => data as APIResponse<Token>);
        })
        .then(async (apiResponse) => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            if (apiResponse.successful && apiResponse.data) {
                localStorage.setItem(
                    "currentUser",
                    JSON.stringify(apiResponse.data)
                );
                currentUserSubject.next(apiResponse.data);

                setApiToken();
            } else {
                authenticationService.logout();
                window.location.reload();
            }
            //await Globals.retrieveMe();

            return apiResponse.data;
        });
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem("currentUser");
    localStorage.removeItem("AUTH_0_FEATURE_FLAG");

    currentUserSubject.next(null);

    Globals.removeMe();
}
